import { createApi } from '@reduxjs/toolkit/query/react';
import { HTTP_METHOD, REDUX_CACHE, AGREEMENTS } from '../constants';
import {
  getBaseQuery,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import { Agreements as AgreementsEndpoints } from '../endpoints';
import {
  AgreementDocumentType,
  IAgreementDocumentItems,
  IAgreementVersion,
} from '../../../types/interfaces/agreement';

type ReorderItemDto = {
  id: string;
  order: number;
};

type AgreementTranslationDto = {
  id?: string;
  languageKey: string;
  value: string;
};

type AgreementItemDto = {
  id: string;
  type: string;
  order?: number;
  translations?: AgreementTranslationDto[];
};

type AgreementVersionActionsDto = {
  canEdit?: boolean;
  canPublish?: boolean;
  canArchive?: boolean;
};

type AgreementVersionDto = {
  id: string;
  type: string;
  version: number;
  status: string;
  actions?: AgreementVersionActionsDto;
};

type AgreementDocumentItemsDto = {
  id: string;
  type: string;
  version: number;
  status: string;
  items?: AgreementItemDto[];
};

export type GetAgreementDocumentVersionsRequest = {
  type: string;
};

export type CreateAgreementVersionRequest = {
  documentId: string;
  sourceVersion?: number;
};

export type PublishAgreementVersionRequest = {
  documentId: string;
};

export type ArchiveAgreementVersionRequest = {
  documentId: string;
};

export type UpdateAgreementItemRequest = {
  itemId: string;
  body: { type: string; translations: AgreementTranslationDto[] };
};

export type CreateAgreementItemRequest = {
  body: {
    documentId: string;
    order?: number;
    type: string;
    translations: AgreementTranslationDto[];
  };
};

export type DeleteAgreementItemRequest = {
  itemId: string;
};

export type ReorderAgreementItemsRequest = {
  body: { items: ReorderItemDto[] };
};

export type GetAgreementDocumentItemsByIdRequest = {
  documentId: string;
};

export type GetAgreementDocumentItemsByTypeVersionRequest = {
  type: string;
  version: number;
};

function requireNonEmpty(value: string, fieldName: string) {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`${fieldName} must be a non-empty string`);
  }
  return value.trim();
}

function sanitizeTranslations(
  translations: AgreementTranslationDto[] | undefined,
): AgreementTranslationDto[] {
  if (!Array.isArray(translations)) return [];
  return translations
    .map((translation) => ({
      id: typeof translation?.id === 'string' ? translation.id : undefined,
      languageKey:
        typeof translation?.languageKey === 'string'
          ? translation.languageKey.trim()
          : '',
      value: typeof translation?.value === 'string' ? translation.value : '',
    }))
    .filter((translation) => !!translation.languageKey);
}

function mapAgreementVersion(dto: AgreementVersionDto): IAgreementVersion {
  return {
    id: requireNonEmpty(dto?.id, 'agreementVersion.id'),
    documentId: requireNonEmpty(dto?.id, 'agreementVersion.documentId'),
    version: typeof dto?.version === 'number' ? dto.version : 0,
    status: typeof dto?.status === 'string' ? dto.status : 'draft',
    actions:
      dto?.actions && typeof dto.actions === 'object'
        ? {
            canEdit:
              typeof dto.actions.canEdit === 'boolean'
                ? dto.actions.canEdit
                : undefined,
            canPublish:
              typeof dto.actions.canPublish === 'boolean'
                ? dto.actions.canPublish
                : undefined,
            canArchive:
              typeof dto.actions.canArchive === 'boolean'
                ? dto.actions.canArchive
                : undefined,
          }
        : undefined,
  };
}

function mapAgreementDocumentItems(
  dto: AgreementDocumentItemsDto,
): IAgreementDocumentItems {
  return {
    id: requireNonEmpty(dto?.id, 'agreementDocument.id'),
    type: requireNonEmpty(
      dto?.type,
      'agreementDocument.type',
    ) as AgreementDocumentType,
    version: typeof dto?.version === 'number' ? dto.version : 0,
    status: typeof dto?.status === 'string' ? dto.status : 'draft',
    items: Array.isArray(dto?.items)
      ? dto.items
          .filter((item) => typeof item?.id === 'string' && !!item.id.trim())
          .map((item) => ({
            id: requireNonEmpty(item.id, 'agreementItem.id'),
            type:
              typeof item.type === 'string' && item.type.trim()
                ? item.type
                : 'other',
            order: typeof item.order === 'number' ? item.order : undefined,
            translations: sanitizeTranslations(item.translations),
          }))
      : [],
  };
}

export const agreementsApi = createApi({
  reducerPath: 'agreementsApi',
  tagTypes: [
    AGREEMENTS.DOCUMENT_VERSIONS,
    AGREEMENTS.DOCUMENT_ITEMS,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(AgreementsEndpoints.ROOT),
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getAgreementDocumentVersions: builder.query<
      IAgreementVersion[],
      GetAgreementDocumentVersionsRequest
    >({
      query({ type }) {
        const documentType = requireNonEmpty(type, 'type');
        return {
          url: AgreementsEndpoints.DOCUMENT_VERSIONS(documentType),
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: (response: AgreementVersionDto[]) =>
        Array.isArray(response) ? response.map(mapAgreementVersion) : [],
      providesTags: (result, error, { type }) => [
        { type: AGREEMENTS.DOCUMENT_VERSIONS, id: type },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getAgreementDocumentItemsById: builder.query<
      IAgreementDocumentItems,
      GetAgreementDocumentItemsByIdRequest
    >({
      query({ documentId }) {
        const safeDocumentId = requireNonEmpty(documentId, 'documentId');
        return {
          url: AgreementsEndpoints.DOCUMENT_ITEMS_BY_ID(safeDocumentId),
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: (response: AgreementDocumentItemsDto) =>
        mapAgreementDocumentItems(response),
      providesTags: (result) => [
        { type: AGREEMENTS.DOCUMENT_ITEMS, id: result?.id || REDUX_CACHE.LIST },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getAgreementDocumentItemsByTypeVersion: builder.query<
      IAgreementDocumentItems,
      GetAgreementDocumentItemsByTypeVersionRequest
    >({
      query({ type, version }) {
        const documentType = requireNonEmpty(type, 'type');
        return {
          url: AgreementsEndpoints.DOCUMENT_ITEMS_BY_TYPE_VERSION(
            documentType,
            version,
          ),
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: (response: AgreementDocumentItemsDto) =>
        mapAgreementDocumentItems(response),
      providesTags: (result) => [
        { type: AGREEMENTS.DOCUMENT_ITEMS, id: result?.id || REDUX_CACHE.LIST },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    createAgreementVersion: builder.mutation<
      IAgreementVersion,
      CreateAgreementVersionRequest
    >({
      query({ documentId, sourceVersion }) {
        const safeDocumentId = requireNonEmpty(documentId, 'documentId');
        return {
          url: AgreementsEndpoints.CREATE_VERSION(safeDocumentId),
          method: HTTP_METHOD.POST,
          body: sourceVersion ? { sourceVersion } : {},
        };
      },
      transformResponse: (response: AgreementVersionDto) =>
        mapAgreementVersion(response),
      invalidatesTags: () => [{ type: AGREEMENTS.DOCUMENT_VERSIONS }],
      ...TransformedResponseWithNotification('Agreement version'),
    }),
    publishAgreementVersion: builder.mutation<
      void,
      PublishAgreementVersionRequest
    >({
      query({ documentId }) {
        const safeDocumentId = requireNonEmpty(documentId, 'documentId');
        return {
          url: AgreementsEndpoints.UPDATE_STATUS(safeDocumentId),
          method: HTTP_METHOD.PUT,
          body: { status: 'published' },
        };
      },
      invalidatesTags: () => [{ type: AGREEMENTS.DOCUMENT_VERSIONS }],
      ...TransformedResponseWithNotification('Agreement version'),
    }),
    archiveAgreementVersion: builder.mutation<
      void,
      ArchiveAgreementVersionRequest
    >({
      query({ documentId }) {
        const safeDocumentId = requireNonEmpty(documentId, 'documentId');
        return {
          url: AgreementsEndpoints.UPDATE_STATUS(safeDocumentId),
          method: HTTP_METHOD.PUT,
          body: { status: 'archived' },
        };
      },
      invalidatesTags: () => [{ type: AGREEMENTS.DOCUMENT_VERSIONS }],
      ...TransformedResponseWithNotification('Agreement version'),
    }),
    createAgreementItem: builder.mutation<void, CreateAgreementItemRequest>({
      query({ body }) {
        return {
          url: AgreementsEndpoints.CREATE_ITEM,
          method: HTTP_METHOD.POST,
          body: {
            documentId: requireNonEmpty(body?.documentId, 'body.documentId'),
            order:
              typeof body?.order === 'number' && body.order >= 0
                ? body.order
                : undefined,
            type: requireNonEmpty(body?.type, 'body.type'),
            translations: sanitizeTranslations(body?.translations),
          },
        };
      },
      invalidatesTags: () => [
        { type: AGREEMENTS.DOCUMENT_VERSIONS },
        { type: AGREEMENTS.DOCUMENT_ITEMS },
      ],
      ...TransformedResponseWithNotification('Agreement block'),
    }),
    updateAgreementItem: builder.mutation<void, UpdateAgreementItemRequest>({
      query({ itemId, body }) {
        const safeItemId = requireNonEmpty(itemId, 'itemId');
        return {
          url: AgreementsEndpoints.UPDATE_ITEM(safeItemId),
          method: HTTP_METHOD.PUT,
          body: {
            type: requireNonEmpty(body?.type, 'body.type'),
            translations: sanitizeTranslations(body?.translations),
          },
        };
      },
      invalidatesTags: () => [
        { type: AGREEMENTS.DOCUMENT_VERSIONS },
        { type: AGREEMENTS.DOCUMENT_ITEMS },
      ],
      ...TransformedResponseWithNotification('Agreement block'),
    }),
    deleteAgreementItem: builder.mutation<void, DeleteAgreementItemRequest>({
      query({ itemId }) {
        const safeItemId = requireNonEmpty(itemId, 'itemId');
        return {
          url: AgreementsEndpoints.DELETE_ITEM(safeItemId),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: () => [
        { type: AGREEMENTS.DOCUMENT_VERSIONS },
        { type: AGREEMENTS.DOCUMENT_ITEMS },
      ],
      ...TransformedResponseWithNotification('Agreement block'),
    }),
    reorderAgreementItems: builder.mutation<void, ReorderAgreementItemsRequest>(
      {
        query({ body }) {
          return {
            url: AgreementsEndpoints.REORDER_ITEMS,
            method: HTTP_METHOD.PUT,
            body: {
              items: Array.isArray(body?.items)
                ? body.items
                    .filter(
                      (item) =>
                        typeof item?.id === 'string' &&
                        !!item.id.trim() &&
                        typeof item?.order === 'number' &&
                        item.order > 0,
                    )
                    .map((item) => ({
                      id: item.id.trim(),
                      order: item.order,
                    }))
                : [],
            },
          };
        },
        invalidatesTags: () => [
          { type: AGREEMENTS.DOCUMENT_VERSIONS },
          { type: AGREEMENTS.DOCUMENT_ITEMS },
        ],
        ...TransformedResponseWithNotification('Agreement blocks order'),
      },
    ),
  }),
});

export const {
  useGetAgreementDocumentVersionsQuery,
  useGetAgreementDocumentItemsByIdQuery,
  useGetAgreementDocumentItemsByTypeVersionQuery,
  useCreateAgreementVersionMutation,
  usePublishAgreementVersionMutation,
  useArchiveAgreementVersionMutation,
  useCreateAgreementItemMutation,
  useUpdateAgreementItemMutation,
  useDeleteAgreementItemMutation,
  useReorderAgreementItemsMutation,
} = agreementsApi;
